import { useNavigate } from "react-router-dom";
import { useNotifications } from "features/notifications";
import {
  useCreateAuditMutation,
  useUploadUrlMutation,
  useRegisterDocumentMutation,
} from "./endpoints";
import {
  RegisterDocumentRequest,
  UploadUrlRequest,
  UploadUrlResponse,
} from "./definitions";
import { Audit } from "models/generateAuditSchemas";
import { getAuditErrorMessage } from "./utils";
import { GaiaConstants } from "utils";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import { FileMetadata } from "components/input-files/definitions";

interface UseCreateAuditParams {
  setDisableCreate: (disabled: boolean) => void;
}

interface UseCreateAuditWithDocParams {
  teamName: string;
  setDisableCreate: (disabled: boolean) => void;
}

/**
 * Custom hook for creating an audit without a document
 *
 * @param params - Configuration parameters
 * @param params.setDisableCreate - Function to disable/enable the create button
 *
 * @returns Function to create an audit
 */
export const useCreateAudit = ({ setDisableCreate }: UseCreateAuditParams) => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [createAudit] = useCreateAuditMutation();

  const onCreateAudit = async (processedAudit: Audit) => {
    try {
      await createAudit(processedAudit).unwrap();
      addNotification({
        type: "success",
        header: "Audit created successfully",
        dismissible: true,
        timeout: 10000,
      });
      navigate("/audits");
    } catch (e: any) {
      const errorMessage = getAuditErrorMessage(
        e,
        "Failed to create the audit"
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: 10000,
      });
      setDisableCreate(false);
    }
  };

  return onCreateAudit;
};

/**
 * Custom hook for creating an audit with multiple document attachments
 *
 * @param params - Configuration parameters
 * @param params.teamName - The team name for the audit
 * @param params.setDisableCreate - Function to disable/enable the create button
 *
 * @returns Function to create an audit with multiple documents
 */
export const useCreateAuditWithMultipleDocs = ({
  teamName,
  setDisableCreate,
}: UseCreateAuditWithDocParams) => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [createAudit] = useCreateAuditMutation();
  const [uploadUrl] = useUploadUrlMutation();
  const [registerDocument] = useRegisterDocumentMutation();

  const onCreateAuditWithMultipleDocs = async (
    processedAudit: Audit,
    fileMetadata: FileMetadata[]
  ) => {
    let auditResponse: any = null;

    // Step 1: Create the audit
    try {
      auditResponse = await createAudit(processedAudit).unwrap();
    } catch (e: any) {
      const errorMessage = getAuditErrorMessage(
        e,
        "Failed to create the audit"
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: 10000,
      });
      setDisableCreate(false);
      return;
    }

    // Step 2: Upload all documents
    try {
      const uploadPromises = fileMetadata.map(async (metadata) => {
        const file = metadata.file;

        // Get upload URL
        const uploadUrlRequest: UploadUrlRequest = {
          prefix: `${GaiaConstants.AUDIT_FILES_PREFIX}/${auditResponse.auditId}`,
          fileName: file.name,
          teamName: teamName,
          securityLevel: "Normal",
        };

        const uploadUrlResponse: UploadUrlResponse = await uploadUrl(
          uploadUrlRequest
        ).unwrap();

        // Upload file to S3
        await fetch(uploadUrlResponse.url, {
          method: "PUT",
          body: file,
          headers: {
            "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
          },
        });

        // Step 3: Register the document
        const s3Prefix = `${GaiaConstants.AUDIT_FILES_PREFIX}/${auditResponse.auditId}/${uploadUrlResponse.uniqueKey}/${file.name}`;
        const registerDocumentRequest: RegisterDocumentRequest = {
          parentId: auditResponse.auditId,
          teamName: teamName,
          fileName: getFileNameWithoutExtension(file.name),
          documentType: metadata.documentType || "Audit notices",
          securityLevel: "Normal",
          s3Prefix: s3Prefix,
          extentionType: getFileExtension(file.name),
          auditId: auditResponse.auditId,
          size: getFileSize(file.size),
        };

        await registerDocument(registerDocumentRequest).unwrap();
      });

      await Promise.all(uploadPromises);

      addNotification({
        type: "success",
        header: `Audit created with ${fileMetadata.length} file${
          fileMetadata.length > 1 ? "s" : ""
        } uploaded successfully`,
        dismissible: true,
        timeout: 10000,
      });
      navigate("/audits");
    } catch (e: any) {
      addNotification({
        type: "warning",
        header: "Audit created successfully, but file upload failed",
        content:
          "The audit was created, but the document could not be uploaded. You can upload the document later from the audit details page.",
        dismissible: true,
        timeout: 15000,
      });
      navigate("/audits");
    }
  };

  return onCreateAuditWithMultipleDocs;
};
